import React, { useEffect, useRef } from 'react';

const SPACING = 20;
const DOT_RADIUS = 0.75;
const HOVER_RADIUS_GROWTH = 0.35;
const INFLUENCE_RADIUS = 110;
const MAX_PUSH = 16;
const SPRING = 0.08;
const DAMPING = 0.82;
const GLOW_EASE = 0.12;
const BASE_ALPHA = 0.1;
const HOVER_ALPHA = 0.45;
const ACCENT_MIX = 0.55;

const BASE_RGB = [255, 255, 255];
const ACCENT_RGB = [253, 197, 45];

const mixChannel = (from, to, t) => Math.round(from + (to - from) * t);

/**
 * Grade de pontos em canvas que se afastam do cursor (efeito ímã) e voltam com mola.
 * Escuta o ponteiro no elemento pai, que precisa ter `position: relative`.
 */
const InteractiveDotGrid = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const host = canvas?.parentElement;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !host || !ctx) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let dots = [];
    let width = 0;
    let height = 0;
    let pointer = null;
    let frame = 0;

    const build = () => {
      const rect = host.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const cols = Math.ceil(width / SPACING) + 1;
      const rows = Math.ceil(height / SPACING) + 1;
      const offsetX = (width - (cols - 1) * SPACING) / 2;
      const offsetY = (height - (rows - 1) * SPACING) / 2;
      dots = [];
      for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
          const ox = offsetX + c * SPACING;
          const oy = offsetY + r * SPACING;
          dots.push({ ox, oy, x: ox, y: oy, vx: 0, vy: 0, glow: 0 });
        }
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const baseFill = `rgba(${BASE_RGB.join(', ')}, ${BASE_ALPHA})`;
      for (const d of dots) {
        if (d.glow < 0.01) {
          ctx.fillStyle = baseFill;
        } else {
          const g = d.glow;
          const rgb = BASE_RGB.map((ch, i) => mixChannel(ch, ACCENT_RGB[i], g * ACCENT_MIX));
          const alpha = BASE_ALPHA + (HOVER_ALPHA - BASE_ALPHA) * g;
          ctx.fillStyle = `rgba(${rgb.join(', ')}, ${alpha})`;
        }
        ctx.beginPath();
        ctx.arc(d.x, d.y, DOT_RADIUS + d.glow * HOVER_RADIUS_GROWTH, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const step = () => {
      let moving = false;
      for (const d of dots) {
        let tx = d.ox;
        let ty = d.oy;
        let targetGlow = 0;

        if (pointer) {
          const dx = d.ox - pointer.x;
          const dy = d.oy - pointer.y;
          const dist = Math.hypot(dx, dy);
          if (dist < INFLUENCE_RADIUS) {
            const strength = 1 - dist / INFLUENCE_RADIUS;
            const push = strength * strength * MAX_PUSH;
            if (dist > 0.001) {
              tx += (dx / dist) * push;
              ty += (dy / dist) * push;
            }
            targetGlow = strength;
          }
        }

        d.vx = (d.vx + (tx - d.x) * SPRING) * DAMPING;
        d.vy = (d.vy + (ty - d.y) * SPRING) * DAMPING;
        d.x += d.vx;
        d.y += d.vy;
        d.glow += (targetGlow - d.glow) * GLOW_EASE;

        if (
          Math.abs(d.vx) > 0.01 ||
          Math.abs(d.vy) > 0.01 ||
          Math.abs(tx - d.x) > 0.05 ||
          Math.abs(ty - d.y) > 0.05 ||
          Math.abs(targetGlow - d.glow) > 0.005
        ) {
          moving = true;
        }
      }
      draw();
      frame = moving ? requestAnimationFrame(step) : 0;
    };

    const startLoop = () => {
      if (!frame) frame = requestAnimationFrame(step);
    };

    const handlePointerMove = (event) => {
      const rect = host.getBoundingClientRect();
      pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      startLoop();
    };

    const handlePointerLeave = () => {
      pointer = null;
      startLoop();
    };

    build();
    draw();

    const resizeObserver = new ResizeObserver(() => {
      build();
      draw();
    });
    resizeObserver.observe(host);

    if (!reduceMotion) {
      host.addEventListener('pointermove', handlePointerMove);
      host.addEventListener('pointerleave', handlePointerLeave);
    }

    return () => {
      if (frame) cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      host.removeEventListener('pointermove', handlePointerMove);
      host.removeEventListener('pointerleave', handlePointerLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none' }}
    />
  );
};

export default InteractiveDotGrid;
